/**
 * Force Password Change Modal
 * Shown when employees or agents log in for the first time
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';
import { Lock, Eye, EyeOff, CheckCircle, AlertTriangle, Loader2, Shield } from 'lucide-react';

const ForcePasswordChange = ({ isOpen, onSuccess, userEmail }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Password strength validation
  const hasMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const isValid = hasMinLength && hasUppercase && hasLowercase && hasNumber && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValid) {
      setError('Please meet all password requirements');
      return;
    }

    setIsSubmitting(true);
    try {
      await authAPI.forceChangePassword(newPassword);
      toast.success('Password changed successfully!');
      onSuccess();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to change password. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const PasswordRequirement = ({ met, text }) => (
    <div className={`flex items-center gap-2 text-sm ${met ? 'text-green-600' : 'text-gray-400'}`}>
      {met ? <CheckCircle className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border-2 border-current" />}
      {text}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md [&>button]:hidden">
        <DialogHeader>
          <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2 text-xl">
            <Shield className="w-6 h-6 text-teal-600" />
            Set Your New Password
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <Alert className="bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800">
            <AlertTriangle className="w-4 h-4 text-teal-600" />
            <AlertDescription className="text-teal-700 dark:text-teal-300 text-sm">
              For security reasons, you must change your password before continuing. This is required for first-time login.
            </AlertDescription>
          </Alert>

          {userEmail && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Account: <span className="font-medium text-gray-700 dark:text-gray-300">{userEmail}</span>
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-700 dark:text-red-300 text-sm">{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 pl-10 pr-10 dark:text-white"
                  placeholder="Enter new password"
                  required
                  data-testid="force-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 pl-10 pr-10 dark:text-white"
                  placeholder="Confirm new password"
                  required
                  data-testid="force-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password Requirements:</p>
              <PasswordRequirement met={hasMinLength} text="At least 8 characters" />
              <PasswordRequirement met={hasUppercase} text="At least one uppercase letter (A-Z)" />
              <PasswordRequirement met={hasLowercase} text="At least one lowercase letter (a-z)" />
              <PasswordRequirement met={hasNumber} text="At least one number (0-9)" />
              <PasswordRequirement met={passwordsMatch} text="Passwords match" />
            </div>

            <Button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3"
              data-testid="force-change-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing Password...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Change Password & Continue
                </>
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordChange;
